import type { Alert, AlertsResponse } from "@paisa/types"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { api } from "@/lib/api"

export type { Alert }

/**
 * Mount once at the root layout — opens an SSE connection and pushes updates
 * into the query cache. `enabled: false` (private mode locked, or mode not
 * known yet) keeps the stream closed, so no alert payload arrives while the
 * veil is up.
 */
export function useAlertsSSE({ enabled = true }: { enabled?: boolean } = {}) {
  const qc = useQueryClient()
  useEffect(() => {
    if (!enabled) return
    // Exponential backoff: 1s → 2s → 4s → 8s → 16s → 30s (cap)
    let retryDelay = 1000
    let retryTimeout: ReturnType<typeof setTimeout> | null = null
    let es: EventSource
    let unmounted = false

    function connect() {
      es = new EventSource("/api/alerts/stream", { withCredentials: true })

      es.addEventListener("alerts", (e) => {
        try {
          qc.setQueryData(["alerts"], JSON.parse((e as MessageEvent).data) as AlertsResponse)
          // Reset backoff on successful message receipt
          retryDelay = 1000
        } catch {}
      })

      es.onerror = async () => {
        es.close()
        if (unmounted) return
        // Check if we're unauthenticated — if so, go to login instead of retrying
        try {
          const probe = await fetch("/health", { credentials: "include" })
          if (probe.status === 401) {
            window.location.href = "/login"
            return
          }
        } catch {}
        retryTimeout = setTimeout(() => {
          if (!unmounted) connect()
        }, retryDelay)
        retryDelay = Math.min(retryDelay * 2, 30_000)
      }
    }

    connect()

    return () => {
      unmounted = true
      if (retryTimeout !== null) {
        clearTimeout(retryTimeout)
      }
      es.close()
    }
  }, [qc, enabled])
}

export function useAlerts() {
  return useQuery({
    queryKey: ["alerts"],
    queryFn: () => api.get<AlertsResponse>("/alerts"),
  })
}

export function useGenerateAlerts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post("/alerts/generate", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  })
}

export function useDismissAlert() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.patch(`/alerts/${id}/dismiss`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  })
}

export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post("/alerts/read-all", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  })
}
